import { useEffect, useRef, useState } from "react";
import { X, Download, Loader2, Sparkles, TrendingUp, Users, Trophy } from "lucide-react";
import * as htmlToImage from "html-to-image";
import api, { fmtNaira } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/lib/i18n";
import { toast } from "sonner";

// WhatsApp brand icon (inline SVG) — keeps bundle small & on-brand
const WhatsAppIcon = ({ className = "w-4 h-4" }) => (
  <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.057 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.002-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884zM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.892c0 2.096.549 4.142 1.594 5.945L0 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.581 0 11.94-5.335 11.943-11.893a11.821 11.821 0 0 0-3.476-8.443z"/>
  </svg>
);

export default function DailyDiary({ onClose }) {
  const { user } = useAuth();
  const t = useT(user?.preferred_language);
  const [data, setData] = useState(null);
  const [tagline, setTagline] = useState("");
  const [busy, setBusy] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    api.get("/diary/today").then((r) => {
      setData(r.data);
      setTagline(r.data.tagline_options[Math.floor(Math.random() * r.data.tagline_options.length)]);
    });
  }, []);

  const downloadImage = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const dataUrl = await htmlToImage.toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = `kobolog-diary-${data.date.replace(/[, ]/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      toast.success("Saved! Now share to WhatsApp Status 📱");
    } catch (e) {
      toast.error("Failed to save image");
    } finally { setBusy(false); }
  };

  const shareWhatsApp = async () => {
    if (!cardRef.current) return;
    setBusy(true);
    try {
      const blob = await htmlToImage.toBlob(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const text = `${tagline} — built with Kobolog AI · kobolog.ai`;

      // 1. Mobile + supports file share → native WhatsApp share sheet (best UX, lets user pick Status)
      if (navigator.canShare && blob && navigator.canShare({ files: [new File([blob], "kobolog-diary.png", { type: "image/png" })] })) {
        const file = new File([blob], "kobolog-diary.png", { type: "image/png" });
        try {
          await navigator.share({ files: [file], title: "My Kobolog Daily Diary", text });
          return;
        } catch (e) {
          // User cancelled the share sheet — fall through to desktop fallback
          console.debug("Share cancelled or unavailable:", e?.name);
        }
      }
      // 2. Desktop / unsupported → open wa.me with prefilled text + auto-download image so user can attach
      const dataUrl = await htmlToImage.toPng(cardRef.current, { pixelRatio: 2, cacheBust: true });
      const link = document.createElement("a");
      link.download = `kobolog-diary-${data.date.replace(/[, ]/g, "-")}.png`;
      link.href = dataUrl;
      link.click();
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, "_blank");
      toast.success("Image saved! Attach it to your WhatsApp Status 📱");
    } catch (e) {
      toast.error("Failed to share");
    } finally { setBusy(false); }
  };

  if (!data) return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center" onClick={onClose}>
      <Loader2 className="w-8 h-8 text-white animate-spin" />
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center overflow-y-auto py-6" onClick={onClose}>
      <div className="relative max-w-sm w-full px-4" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} className="absolute -top-1 -right-1 z-10 w-9 h-9 rounded-full bg-white/90 flex items-center justify-center shadow-lg" data-testid="close-diary"><X className="w-4 h-4" /></button>

        {/* The 9:16 card (canvas-rendered) */}
        <div
          ref={cardRef}
          className="relative w-full aspect-[9/16] rounded-2xl overflow-hidden text-white shadow-2xl"
          style={{ background: "linear-gradient(155deg, #115E3E 0%, #0C4A30 45%, #D96C4A 100%)" }}
          data-testid="diary-card"
        >
          {/* Texture overlay */}
          <div className="absolute inset-0 opacity-20" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='80' height='80'%3E%3Cfilter id='n'%3E%3CfeTurbulence baseFrequency='0.85' /%3E%3CfeColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 0.08 0'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`
          }} aria-hidden />

          <div className="absolute inset-0 flex flex-col p-6">
            {/* Top */}
            <div className="flex items-start justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-[0.2em] opacity-70">Daily Diary</div>
                <div className="text-sm font-medium opacity-90 mt-0.5">{data.date}</div>
              </div>
              <div className="flex items-center gap-1.5 bg-white/15 backdrop-blur rounded-full px-2 py-1">
                <Sparkles className="w-3 h-3" />
                <span className="text-[10px] font-bold tracking-wider">KOBOLOG AI</span>
              </div>
            </div>

            {/* Owner */}
            <div className="mt-6">
              <div className="font-heading text-2xl font-bold leading-tight">{data.owner_name}</div>
              {data.business_name && <div className="text-sm opacity-80 mt-0.5">{data.business_name}</div>}
            </div>

            {/* Hero — today's profit */}
            <div className="mt-5">
              <div className="text-[10px] uppercase tracking-wider opacity-70">Today's profit</div>
              <div className="font-heading text-[44px] font-bold leading-none mt-1 tabular-nums">
                {data.today_profit >= 0 ? "+" : ""}{fmtNaira(data.today_profit)}
              </div>
            </div>

            {/* Stats grid */}
            <div className="mt-5 grid grid-cols-2 gap-2.5 text-sm">
              <Stat icon={TrendingUp} label="Sales today" value={fmtNaira(data.today_sales)} />
              <Stat icon={Users} label="Customers" value={data.customers_today} />
              <Stat icon={Sparkles} label="Top item" value={data.top_item || "—"} />
              <Stat icon={Trophy} label="Streak" value={`${data.streak_days} day${data.streak_days !== 1 ? "s" : ""}`} />
            </div>

            {/* Score */}
            <div className="mt-4 bg-white/15 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
              <div className="text-4xl">{data.tier.emoji}</div>
              <div className="flex-1">
                <div className="text-[10px] uppercase tracking-wider opacity-75">Kobolog Score</div>
                <div className="font-heading text-2xl font-bold tabular-nums leading-tight">{data.score} <span className="text-xs opacity-70">/ 1000</span></div>
                <div className="text-xs opacity-80">{data.tier.name} tier</div>
              </div>
            </div>

            <div className="mt-auto">
              <div className="text-sm font-semibold italic">"{tagline}"</div>
              <div className="text-[10px] uppercase tracking-[0.25em] opacity-60 mt-2">kobolog.ai · every kobo, logged</div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-3 grid grid-cols-2 gap-2">
          <button onClick={downloadImage} disabled={busy} className="bg-white text-kudi-green rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-kudi-cream transition disabled:opacity-50" data-testid="download-diary">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Download className="w-4 h-4" /> {t("saveImg")}</>}
          </button>
          <button onClick={shareWhatsApp} disabled={busy} className="bg-emerald-600 text-white rounded-full py-3 font-semibold flex items-center justify-center gap-2 hover:bg-emerald-700 transition disabled:opacity-50" data-testid="share-diary">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><WhatsAppIcon className="w-4 h-4" /> {t("whatsappShare")}</>}
          </button>
        </div>
        <p className="text-center text-xs text-white/70 mt-3">Post as WhatsApp Status for instant social proof 🚀</p>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-white/15 backdrop-blur rounded-xl p-2.5">
      <div className="flex items-center gap-1 text-[9px] uppercase tracking-wider opacity-70 font-semibold">
        <Icon className="w-2.5 h-2.5" /> {label}
      </div>
      <div className="font-heading font-bold tabular-nums mt-0.5 truncate">{value}</div>
    </div>
  );
}
